import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { PlanSwitcherComponent } from '../plan-switcher/plan-switcher.component';
import { RouterLinkActive, RouterLink, Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { UserFooterComponent } from '../user-footer/user-footer.component';
import { TranslateModule } from '@ngx-translate/core';
import { CommonApiService } from '../../core/services/common-api.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from '../../../environments/environment';
import { CommonModule } from '@angular/common';
import { CategoryOperations, FormOperations, ParticipantOperations, QuestionOperations, RecipientOperations, RuleOperations, SectionId, SubSectionId, TemplateOperations, VariableOperations } from '@src/app/shared/constants/role-permission-constants';


@Component({
  selector: 'app-document-sidebar',
  standalone: true,
  imports: [CommonModule, PlanSwitcherComponent, RouterLinkActive, RouterLink, UserFooterComponent, TranslateModule],
  templateUrl: './document-sidebar.component.html',
  styleUrl: './document-sidebar.component.css'
})
export class DocumentSidebarComponent implements OnInit, OnDestroy {

  isCollapsed = false;
  window: any;
  event: any;
  private clickListener!: (event: MouseEvent) => void;
  private routerSub?: Subscription;
  userId = signal<any>('');
  userName = signal<any>('');
  isAdvanceMenuOpen = false;
  isAdvanceSectionActive = false;
  private readonly advanceSubRoutes = [
    '/advance/documents',
    '/advance/question',
    '/advance/dynamic-functions',
    '/advance/rules',
    '/advance/template',
    '/advance/messaging-template',
    '/advance/participant',
    '/advance/categories'
  ];
  hasFormWizSubscription = false;
  hasNormalSubscription = false;
  isSuperAdminLogin = false;

  // Permissions flags
  permissions = {
    canViewQuestions: false,
    canViewParticipants: false,
    canViewRules: false,
    canViewTemplates: false,
    canViewDocuments: false,
    canViewCategories: false,
    canViewDynamicFunctions: false,
    canViewMessagingTemplates: false,
    canViewDisposedDocuments: false,
    canViewRecipients:false
  };

  hasEssentialFormAccess: any = false;

  constructor(private router: Router, private api: CommonApiService, private toastr: ToastrService) {
  }

  ngOnInit() {

    this.userId.set(Number(localStorage.getItem('userId')));
    this.userName.set(localStorage.getItem('userName'));
    if (environment.isSuperAdmin) {
      this.hasFormWizSubscription = true;
      this.hasNormalSubscription = true;
      this.setAllPermissionsTrue();
      this.isSuperAdminLogin = true;
    } else {
      let spUser: any = localStorage.getItem('sp-user');
      if (spUser) {
        try {
          spUser = JSON.parse(spUser);
          this.hasFormWizSubscription = spUser?.hasFormWizSubscription === true;
          this.hasNormalSubscription = this.hasFormWizSubscription || spUser?.hasNormalSubscription === true;
        } catch {
          this.hasFormWizSubscription = false;
          this.hasNormalSubscription = false;
        }
      }

      // Check permissions from localStorage
      this.checkPermissions();

    }

    this.syncAdvanceMenuForRoute(this.router.url);
    this.routerSub = this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.syncAdvanceMenuForRoute(event.urlAfterRedirects));

    // Load saved state
    this.isCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';

    // proper event listener with reference
    this.clickListener = (event: MouseEvent) => this.handleAutoClose(event);
    document.addEventListener('click', this.clickListener);

  }

  // Set all permissions to true for super admin
  setAllPermissionsTrue() {
    this.permissions.canViewQuestions = true;
    this.permissions.canViewParticipants = true;
    this.permissions.canViewRules = true;
    this.permissions.canViewTemplates = true;
    this.permissions.canViewDocuments = true;
    this.permissions.canViewCategories = true;
    this.permissions.canViewDynamicFunctions = true;
    this.permissions.canViewMessagingTemplates = true;
    this.permissions.canViewDisposedDocuments = true;
    this.permissions.canViewRecipients = true;
  }

  // Check permissions from localStorage
  checkPermissions() {
    let user_data: any = localStorage.getItem('permission_data');
    if (user_data) {
      try {
        user_data = JSON.parse(user_data);
      } catch {
        return;
      }
      if (!Array.isArray(user_data)) {
        return;
      }

      let fullyPermitted = user_data.some((permission: any) => {
        return permission.sectionId === SectionId.FormWiz &&
          permission.subSectionId === 0 &&
          permission.subSectionOperationId === 0;
      });
      
      if (!fullyPermitted) {

        // Get EssentialForm permissions data
        const essentialFormData = user_data.filter((item: any) => 
          item.subSectionId == SubSectionId.EssentialForm
        );

        // Get Form permissions data
        const formData = user_data.filter((item: any) => 
          item.subSectionId == SubSectionId.Form
        );

        // Check if both EssentialForm AND Form exist
        const hasEssentialForm = essentialFormData.length > 0;
        const hasForm = formData.length > 0;

        // hasEssentialFormAccess is true ONLY when EssentialForm exists AND Form does NOT exist
        this.hasEssentialFormAccess = hasEssentialForm && !hasForm;

        // Check Questions permission
        let questionData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Question);
        this.permissions.canViewQuestions = questionData.some((item: any) => { 
          return item.subSectionOperationId == QuestionOperations.Get;
        });
        
        // Check Participants permission
        let participantData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Participant);
        this.permissions.canViewParticipants = participantData.some((item: any) => { 
          return item.subSectionOperationId == ParticipantOperations.Get;
        });
        
        // Check Rules permission
        let ruleData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Rule);
        this.permissions.canViewRules = ruleData.some((item: any) => { 
          return item.subSectionOperationId == RuleOperations.Get;
        });
        
        // Check Templates permission
        let templateData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Template);
        this.permissions.canViewTemplates = templateData.some((item: any) => { 
          return item.subSectionOperationId == TemplateOperations.Get;
        });
        
        // Check Documents permission
        let documentData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Form);
        this.permissions.canViewDocuments = documentData.some((item: any) => { 
          return item.subSectionOperationId == FormOperations.Get;
        });
        
        // Check Categories permission
        let categoryData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Category);
        this.permissions.canViewCategories = categoryData.some((item: any) => { 
          return item.subSectionOperationId == CategoryOperations.Get;
        });
        
        // Check Dynamic Functions permission
        let dynamicFunctionData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Variable);
        this.permissions.canViewDynamicFunctions = dynamicFunctionData.some((item: any) => { 
          return item.subSectionOperationId == VariableOperations.Get;
        });
        
        // Check Messaging Templates permission (using Template section)
        let messagingTemplateData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Template);
        this.permissions.canViewMessagingTemplates = messagingTemplateData.some((item: any) => { 
          return item.subSectionOperationId == TemplateOperations.Get;
        });

        //recipient permission check (same as participant get)
        let recipientData = user_data.filter((item: any) => item.subSectionId == SubSectionId.Recipient);
        this.permissions.canViewRecipients = recipientData.some((item: any) => { 
          return item.subSectionOperationId == RecipientOperations.Get;
        });
        
        // Check Disposed Documents permission (same as Documents)
        this.permissions.canViewDisposedDocuments = this.permissions.canViewDocuments;

      } else {
        // Fully permitted user can see everything
        this.setAllPermissionsTrue();
      }
    }
  }


  get hasFormWizSubscriptionTrue(): boolean {
    return this.hasFormWizSubscription;
  }

  get hasNormalOrFormWizSubscriptionTrue(): boolean {
    return this.hasFormWizSubscription || this.hasNormalSubscription;
  }


  toggleSidebar() {
    const sidebar = document.getElementById('sidebar');
    const overlay = document.getElementById('sidebar-overlay');

    if (!sidebar) return;

    sidebar.classList.toggle('-translate-x-full');
    overlay?.classList.toggle('hidden');
  }

  // FIXED (no window.event)
  handleAutoClose(event: MouseEvent) {
    const target = event.target as HTMLElement;
    const isSidebarLink = target?.closest('#sidebar a');
    const isMobile = window.innerWidth < 1024;

    if (isSidebarLink && isMobile) {
      this.toggleSidebar();
    }
  }

  toggleSidebarCollapse() {
    const sidebar = document.getElementById('sidebar');
    const mainContent = document.getElementById('main-content');
    const collapseIcon = document.getElementById('collapse-icon');
    const sidebarTexts = document.querySelectorAll('.sidebar-text');
    const navBadges = document.querySelectorAll('.nav-badge');

    if (!sidebar) return;

    const isCollapsed = sidebar.classList.toggle('sidebar-collapsed');

    // Sidebar width control (IMPORTANT)
    sidebar.classList.toggle('w-20', isCollapsed);
    sidebar.classList.toggle('w-64', !isCollapsed);

    //Main content spacing
    if (mainContent) {
      mainContent.classList.toggle('lg:ml-20', isCollapsed);
      mainContent.classList.toggle('lg:ml-64', !isCollapsed);
    }

    // Icon toggle
    if (collapseIcon) {
      collapseIcon.classList.toggle('fa-chevron-right', isCollapsed);
      collapseIcon.classList.toggle('fa-chevron-left', !isCollapsed);
    }

    // Hide text
    sidebarTexts.forEach((el) => {
      el.classList.toggle('hidden', isCollapsed);
    });

    // Hide badges
    navBadges.forEach((el) => {
      el.classList.toggle('hidden', isCollapsed);
    });

    //Save state
    localStorage.setItem('sidebarCollapsed', String(isCollapsed));
  }

  ngOnDestroy() {
    // correctly removes listener
    document.removeEventListener('click', this.clickListener);
    this.routerSub?.unsubscribe();
  }

  toggleAdvanceMenu() {
    this.isAdvanceMenuOpen = !this.isAdvanceMenuOpen;
  }

  private syncAdvanceMenuForRoute(url: string) {
    const path = url.split('?')[0];
    const onAdvanceHome = path === '/advance' || path === '/advance/';
    const onAdvanceChild = this.advanceSubRoutes.some((route) => path === route || path.startsWith(route + '/'));
    this.isAdvanceSectionActive = onAdvanceHome || onAdvanceChild;
    if (onAdvanceHome || onAdvanceChild) {
      this.isAdvanceMenuOpen = true;
    }
  }

  goToDashboard(keyLink: string) {
    this.api.allPostMethod('AccountSwitchAToB/GetToken', { userId: this.userId() }, true).subscribe({
      next: (res: any) => {
        if (!res['error'] && res['data'] && res['data']['abToken']) {
          let adminUrl: string = environment.blazzerUrl;
          let url = `${adminUrl}/verify-token?ABToken=${encodeURIComponent(res['data']['abToken'])}&ReturnUrl=${encodeURIComponent(`/${keyLink}`)}`;

          //window.open(url)
          window.location.href = url;
        } else {
          this.toastr.error(res.message || 'Something went wrong!!');
        }
      },
      error: (error) => {
        this.toastr.error(error.message || 'Something went wrong!!');
      }
    });
  }

}
